import React from 'react';
import { useQuery } from '@apollo/client';
import { useLocation } from 'react-router';
import { DefaultMeta } from 'src/components/AppShell/Head';
import { useDeps } from 'src/hooks/useDeps';
import gql from 'graphql-tag';
import { CategoryQueryProvider } from 'src/components/CategoryListingPage/CategoryQueryProvider';
import { useSetPopupPageCategory } from 'src/components/PopupBanner/PopupPageContext';
import { CategoryLandingStructuredData } from 'src/components/CategoryLandingPage/CategoryLandingStructuredData';
import { currentUrl } from 'src/util/currentUrl';
import { formatUrl } from 'src/util/formatUrl';
import { useSetResolved } from 'src/core/engine/router/useSetResolved';
import { HomeMainWidgetsSkeleton } from 'src/elements/Skeletons/Skeletons';

import { ErrorOrOfflineConnected } from '../../components/Error/ErrorOrOffline';
import {
    categoryList,
    categoryList_categoryList_children,
    categoryListVariables,
} from '../../queries/__generated__/categoryList';
import { cleanCategories } from '../../components/CategoryList/categoryListUtils';
import { CategoryLandingPage } from '../../components/CategoryLandingPage/CategoryLandingPage';
import '../../scss/_root-component.scss';
import { Wrapper } from '../../components/Layout/Wrapper';

const categoryListQuery = gql`
    query categoryList($id: Int = 6) {
        categoryList: category(id: $id) {
            name
            description
            short_description
            image
            meta_title
            meta_description
            meta_keywords
            hero_banner_mobile
            hero_banner_desktop
            featured_sku
            featured_printed_sku
            featured_embroidered_sku
            extended_description
            faq
            children {
                name
                url_path
                url_key
                image
                id
                is_featured
            }
            brands
            related_categories
        }
    }
`;

type Props = {
    id: number;
    pathname: string;
};

const EMPTY_PARENT_IDS: number[] = [];

export const CategoryLandingRoot: React.FC<Props> = ({ id }) => {
    useSetPopupPageCategory(id, EMPTY_PARENT_IDS);
    useSetResolved();

    const { pathname } = useLocation();
    const { DOMAIN, SSR_GQL } = useDeps().env;

    const { loading, error, data } = useQuery<categoryList, categoryListVariables>(categoryListQuery, {
        variables: { id: id },
        ssr: SSR_GQL,
    });

    if (error) return <ErrorOrOfflineConnected message={`Data fetch error ${error}`} />;
    if (loading || !data) return <HomeMainWidgetsSkeleton />;
    if (!data.categoryList) return null;
    if (!data.categoryList.children) return null;

    const { name, meta_title, meta_description } = data.categoryList;
    const categoryName = data.categoryList.name;
    const categoryFeaturedChildren = data.categoryList.children;
    const categoryDescription = data.categoryList.description;
    const categoryShortDescription = data.categoryList.short_description;
    const heroDesk = data.categoryList.hero_banner_desktop;
    const heroMob = data.categoryList.hero_banner_mobile;
    const categoryId = data.categoryList.children[0]?.id;

    const faq = data.categoryList.faq ? JSON.parse(data.categoryList.faq) : [];
    const brandsRaw = data.categoryList.brands ? JSON.parse(data.categoryList.brands) : [];
    const brands = brandsRaw?.map((brand: { label: string; image: { url: string }[] }) => ({
        label: brand.label,
        image: brand.image?.[0]?.url || '',
    }));

    const relatedCategories = data.categoryList.related_categories
        ? data.categoryList.related_categories.split(',')?.map((id) => id.trim())
        : [];

    const meta = {
        title: meta_title || name || 'unknown',
        description: meta_description || name || 'unknown',
    };

    const cleanedCategories = cleanCategories(categoryFeaturedChildren as categoryList_categoryList_children[]);
    const canonicalUrl = currentUrl(DOMAIN!, pathname);
    const subcategoryLinks = cleanedCategories.map((category) => ({
        name: category.title,
        url: `https://${DOMAIN}${formatUrl(category.link)}`,
    }));
    const breadcrumbs = [
        { name: 'Home', url: `https://${DOMAIN}/` },
        { name: categoryName || name || 'unknown', url: canonicalUrl },
    ];

    const categoriesForFullView = ['Trousers', 'PPE', 'Footwear'];
    const displayFullView = categoriesForFullView.includes(categoryName || '');

    return (
        <Wrapper variant="widget-wrapper-no-padding">
            <DefaultMeta data={meta} withoutPrefix />
            <CategoryLandingStructuredData
                name={categoryName || name || 'unknown'}
                description={meta_description || name || 'unknown'}
                canonicalUrl={canonicalUrl}
                breadcrumbs={breadcrumbs}
                subcategories={subcategoryLinks}
            />
            <CategoryQueryProvider id={id}>
                <CategoryLandingPage
                    name={categoryName!}
                    description={categoryDescription}
                    shortDescription={categoryShortDescription}
                    categories={cleanedCategories}
                    heroDesk={heroDesk}
                    heroMob={heroMob}
                    featuredSku={data.categoryList.featured_sku}
                    featuredPrintedSku={data.categoryList.featured_printed_sku}
                    featuredEmbroideredSku={data.categoryList.featured_embroidered_sku}
                    extendedDescription={data.categoryList.extended_description}
                    categoryId={categoryId}
                    brands={brands}
                    relatedCategories={relatedCategories}
                    faq={faq}
                    displayFullView={displayFullView}
                />
            </CategoryQueryProvider>
        </Wrapper>
    );
};

export default CategoryLandingRoot;
